import { red } from '@mui/material/colors';
import type { StatusTone } from 'config/statusTone';

// Raw design tokens: the one place literal colour values live. Everything else
// reads them through the theme (`'brand.main'` in `sx`, `theme.vars.palette.*`),
// so light/dark switching and the admin/portal split stay in this folder.
// See work_with_claude/design_system_plan.md.

/**
 * Brand teal. Its own palette key, not `primary`: the admin theme's primary is
 * grey, yet admin dashboards and charts carry the teal accent too.
 */
export const BRAND = {
  light: { main: '#0d9488', light: '#2dd4bf', dark: '#0f766e', contrastText: '#ffffff' },
  dark: { main: '#2dd4bf', light: '#5eead4', dark: '#0d9488', contrastText: '#042f2e' },
} as const;

/** Required-field indicator: blue, distinct from error red. */
export const REQUIRED = {
  light: { main: '#1976d2' },
  dark: { main: '#64b5f6' },
} as const;

export const ERROR = { main: red.A400 } as const;

/**
 * Status tones as concrete colours, for charts and KPI cards: Recharts props
 * and hex-suffix tints need real values, not CSS variables. They equal the
 * theme's palette (createAppTheme.test.ts checks it), so a chart's red is the
 * chip's red. Neutral is grey 600 / 500, the shade that clears 3:1 on each
 * surface. Read them with hooks/useToneColors.
 */
export const TONE_COLORS: Record<'light' | 'dark', Record<StatusTone, string>> = {
  light: { neutral: '#757575', info: '#0288d1', warning: '#ed6c02', success: '#2e7d32', error: ERROR.main },
  dark: { neutral: '#9e9e9e', info: '#29b6f6', warning: '#ffa726', success: '#66bb6a', error: ERROR.main },
};

/** Admin chrome: neutral grey. Light-gray primary on dark so buttons read well. */
export const ADMIN_PRIMARY = {
  light: { main: '#424242', light: '#616161', dark: '#212121', contrastText: '#ffffff' },
  dark: { main: '#bdbdbd', light: '#e0e0e0', dark: '#9e9e9e', contrastText: '#212121' },
} as const;

export const ADMIN_SECONDARY = {
  light: { main: '#cccccc' },
  dark: { main: '#757575' },
} as const;

export const PORTAL_SECONDARY = {
  light: { main: '#f5f5f5' },
  dark: { main: '#616161' },
} as const;

export const ADMIN_BACKGROUND_LIGHT = { default: '#fefefe' } as const;
export const PORTAL_BACKGROUND_LIGHT = { default: '#fafafa' } as const;
export const BACKGROUND_DARK = { default: '#121212', paper: '#1e1e1e' } as const;

/**
 * Chart accents: the colour a *metric* is drawn in (p50 latency blue, average
 * orange, processed count green …), shared by every statistics page so the same
 * metric keeps its hue. Not statuses — those are TONE_COLORS. Each hue clears
 * 3:1 on its surface; teal is the brand teal. `muted` (faint background series,
 * drawn translucent) and `surface` (the panel background, for band-chart
 * cut-outs) are not accents. Read with hooks/useChartAccents.
 */
export interface ChartAccents {
  blue: string;
  sky: string;
  teal: string;
  violet: string;
  indigo: string;
  orange: string;
  green: string;
  muted: string;
  surface: string;
}

export const CHART_ACCENTS: Record<'light' | 'dark', ChartAccents> = {
  light: {
    blue: '#1976d2',
    sky: '#0277bd',
    teal: BRAND.light.main,
    violet: '#7c3aed',
    indigo: '#4f46e5',
    orange: '#e65100',
    green: '#2e7d32',
    muted: '#cccccc',
    surface: '#ffffff',
  },
  dark: {
    blue: '#64b5f6',
    sky: '#4fc3f7',
    teal: BRAND.dark.main,
    violet: '#a78bfa',
    indigo: '#818cf8',
    orange: '#ffb74d',
    green: '#81c784',
    muted: '#757575',
    surface: BACKGROUND_DARK.paper,
  },
};

/**
 * Emphasis weight. MUI's own keys keep their meaning (Medium 500, Bold 700);
 * new emphasis uses this one. In `sx`: `fontWeight: 'fontWeightSemibold'`.
 */
export const FONT_WEIGHT_SEMIBOLD = 600;

/**
 * Icon sizes in px, for `sx={{ fontSize: ICON_SIZE.sm }}` on an icon (`badge`:
 * the icon inside a 48px dashboard badge circle). Prefer
 * the icon's own `fontSize="small" | "large"` prop when one of those fits.
 */
export const ICON_SIZE = { sm: 16, md: 20, lg: 24, badge: 32, xl: 48 } as const;

/**
 * MUI's own body1 / body2 / caption sizes as constants, for places outside MUI's `sx`
 * where `theme.typography.*` is not at hand: CodeMirror `EditorView.theme`
 * configs, React `style` props. createAppTheme.test.ts checks they equal the
 * theme's.
 */
export const FONT_SIZE = { body1: '1rem', body2: '0.875rem', caption: '0.75rem' } as const;

/**
 * Text drawn inside charts (SVG), where Typography variants do not reach. px.
 * tick = axis ticks and slice labels (caption size); label = reference-line
 * labels (micro size); mark = a glyph inside a 10px chart marker.
 */
export const CHART_TEXT = { tick: 12, label: 11, mark: 8 } as const;

export const FONT_FAMILY = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

/**
 * Two sizes the app kept inventing between `caption` (0.75rem) and `body2`
 * (0.875rem). In `sx`: `typography: 'label'`, or
 * `fontSize: TYPE_SCALE.label.fontSize` when line-height and weight must stay
 * (a constant, so it also works where no app theme is provided, e.g. tests;
 * `theme.typography.label` does not exist on MUI's default theme). They set fontFamily like MUI's own variants do, so a label inside
 * a monospace block or a heading still resets to the body font.
 */
export const TYPE_SCALE = {
  /** Table cells, chip-adjacent labels, chart tooltips (was 0.8 / 0.8125 / 0.85rem / 13px). */
  label: { fontFamily: FONT_FAMILY, fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.43 },
  /** Badges, counters (was 0.65 / 0.68 / 0.7rem). */
  micro: { fontFamily: FONT_FAMILY, fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.45 },
} as const;

/**
 * base16 palette for react-json-tree (a third-party theme format). Light only;
 * callers pass `invertTheme` in dark mode.
 */
export const JSON_VIEWER_THEME = {
  base00: '#ffffff',
  base01: '#f5f5f5',
  base02: '#e0e0e0',
  base03: '#bdbdbd',
  base04: '#9e9e9e',
  base05: '#424242',
  base06: '#212121',
  base07: '#000000',
  base08: '#e53935',
  base09: '#f76d47',
  base0A: '#ffb300',
  base0B: '#7cb342',
  base0C: '#39adb5',
  base0D: '#1e88e5',
  base0E: '#8e24aa',
  base0F: '#d81b60',
} as const;
